import React, { useState } from "react";
import { growthQuestions } from "./questions";
import { GrowthResult } from "./GrowthQuiz";
import {
  Lightbulb,
  ConnectWithoutContact,
  EmojiEvents,
  ArrowForward,
  CheckCircle,
} from "@mui/icons-material";

interface GrowthQuizContainerProps {
  onQuizComplete: (results: GrowthResult[]) => void;
}

const GrowthQuizContainer: React.FC<GrowthQuizContainerProps> = ({
  onQuizComplete,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [results, setResults] = useState<GrowthResult[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const question = growthQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / growthQuestions.length) * 100;

  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = () => {
    if (selectedOption === null) return;

    const newResult: GrowthResult = {
      questionId: question.id,
      question: question.question,
      answer: question.options[selectedOption].text,
      scores: question.options[selectedOption].scores,
    };

    const updatedResults = [...results, newResult];
    setResults(updatedResults);

    if (currentQuestion < growthQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    } else {
      onQuizComplete(updatedResults);
    }
  };

  const getCategoryIcon = () => {
    const icons = [
      <Lightbulb key="see" />,
      <ConnectWithoutContact key="bridge" />,
      <EmojiEvents key="be" />,
    ];
    return icons[currentQuestion % icons.length];
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "3rem",
          maxWidth: "800px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Progress Bar */}
        <div style={{ marginBottom: "2rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#c9a750",
              }}
            >
              Question {currentQuestion + 1} of {growthQuestions.length}
            </span>
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#c9a750",
              }}
            >
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#f5f5dc",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                background: "#c9a750",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Question Header */}
        <div style={{ marginBottom: "2rem", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "#c9a750",
              color: "white",
              fontSize: "1.5rem",
              marginBottom: "1rem",
            }}
          >
            {getCategoryIcon()}
          </div>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#1a202c",
              marginBottom: "0.5rem",
              lineHeight: "1.4",
            }}
          >
            {question.question}
          </h2>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#718096",
            }}
          >
            Choose the option that resonates most with you
          </p>
        </div>

        {/* Options */}
        <div style={{ marginBottom: "2rem" }}>
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionSelect(index)}
              style={{
                width: "100%",
                padding: "1.25rem",
                marginBottom: "1rem",
                fontSize: "1rem",
                fontWeight: "500",
                color: selectedOption === index ? "#c9a750" : "#4a5568",
                backgroundColor:
                  selectedOption === index ? "#fefce8" : "#f7fafc",
                border:
                  selectedOption === index
                    ? "2px solid #c9a750"
                    : "2px solid #e2e8f0",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onMouseEnter={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.backgroundColor = "#fefce8";
                  e.currentTarget.style.borderColor = "#e4d6a7";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.backgroundColor = "#f7fafc";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }
              }}
            >
              <span>{option.text}</span>
              {selectedOption === index && (
                <CheckCircle
                  style={{
                    color: "#c9a750",
                    fontSize: "1.5rem",
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={selectedOption === null}
          style={{
            width: "100%",
            padding: "1rem 2rem",
            fontSize: "1.1rem",
            fontWeight: "600",
            color: "white",
            background: selectedOption !== null ? "#c9a750" : "#cbd5e0",
            border: "none",
            borderRadius: "12px",
            cursor: selectedOption !== null ? "pointer" : "not-allowed",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            boxShadow:
              selectedOption !== null
                ? "0 4px 15px rgba(201, 167, 80, 0.4)"
                : "none",
          }}
          onMouseEnter={(e) => {
            if (selectedOption !== null) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(201, 167, 80, 0.5)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedOption !== null) {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(201, 167, 80, 0.4)";
            }
          }}
        >
          {currentQuestion < growthQuestions.length - 1
            ? "Next Question"
            : "Discover My Focus"}
          <ArrowForward />
        </button>

        {/* Helper Text */}
        <p
          style={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#a0aec0",
            marginTop: "1rem",
          }}
        >
          Your answers help identify your primary growth opportunity
        </p>
      </div>
    </div>
  );
};

export default GrowthQuizContainer;
